import { isCarsTrucks } from '@kijiji/category'
import { useDecision } from '@optimizely/react-sdk'
import dynamic from 'next/dynamic'
import { useTranslation } from 'next-i18next'
import { useEffect, useMemo } from 'react'
import { useTheme } from 'styled-components'

import { SrpPagination } from '@/components/srp/pagination'
import { PopularRelatedKeywords } from '@/components/srp/popular-related-keywords/PopularRelatedKeywords'
import { RenderListingCard } from '@/components/srp/search-list/RenderListingCard'
import { RenderTopAds } from '@/components/srp/search-list/RenderTopAds'
import {
  type SearchListContainerProps,
  SearchListContainer,
} from '@/components/srp/search-list/styled'
import { SrpLoading } from '@/components/srp/srp-loading'
import { SRP_LISTING_LIMIT } from '@/constants/pageSettings'
import { getSearchCategoryFromSearchQuery } from '@/domain/category/getSearchCategoryFromSearchQuery'
import { getUserLocationFromSearchQuery } from '@/domain/location/getUserLocationFromSearchQuery'
import { isICOLaunchedInLocation } from '@/domain/location/isICOLaunchedInLocation'
import {
  getExtendedRadius,
  hasExtendedRadiusListings,
  isLowInventorySearch,
  isZeroInventorySearch,
} from '@/domain/lowInventory'
import { getCurrentPageFromPagination } from '@/domain/srp/getCurrentPageFromPaginationData'
import { AdSense } from '@/features/advertisement/components/adsense/AdSense'
import { ImageSeoJsonLdWrapper } from '@/features/seo/components/ImageSeoJsonLdWrapper'
import { useGetSearchResultsData } from '@/hooks/srp/useGetSearchResultsData'
import { useSearchListCrossPromoConfig } from '@/hooks/useSearchListCrossPromoConfig'
import { trackEvent } from '@/lib/ga'
import { getSearchPageType } from '@/lib/ga/events/search'
import { FEATURE_FLAG } from '@/lib/optimizely'
import { type Listing } from '@/types/search'
import { Flex } from '@/ui/atoms/flex'
import { HeadlineText } from '@/ui/atoms/headline-text'
import { Spacing } from '@/ui/atoms/spacing'

const ZeroResultsActionBox = dynamic(
  () => import('@/components/srp/zero-results/').then((mod) => mod.ZeroResultsActionBox),
  { ssr: true } // to prevent layout shift
)

const MiniLeaderboardAdSlot = dynamic(
  () =>
    import('@/components-page/srp/advertisement/MiniLeaderboardAdSlot').then(
      (mod) => mod.MiniLeaderboardAdSlot
    ),
  { ssr: false }
)

const PaginationLinkContainer = dynamic(
  () => import('./styled').then((mod) => mod.PaginationLinkContainer),
  { ssr: false }
)

export type SearchListProps = {
  extendedRadiusListings: Listing[]
  totalPageCount: number
  isMobile?: boolean
}

const autosSearchListContainerGap: SearchListContainerProps['gap'] = {
  small: 'defaultSmall',
  xLarge: 'default',
}

export const SearchList = ({
  extendedRadiusListings = [],
  totalPageCount,
  isMobile,
}: SearchListProps) => {
  const { colors, spacing } = useTheme()

  const { t } = useTranslation(['srp', 'common'])

  const { data, loadingResults: loading } = useGetSearchResultsData()

  const { pagination, searchQuery, results } = data || {}
  const { topListings: topAds = [], mainListings: listings = [] } = results || {}
  const globalKeyword = searchQuery?.keywords || ''
  const { totalCount: totalListings = 0, offset = 0, limit = SRP_LISTING_LIMIT } = pagination || {}

  const currentPage = getCurrentPageFromPagination({ offset, limit })

  const location = getUserLocationFromSearchQuery(searchQuery)
  const category = getSearchCategoryFromSearchQuery(searchQuery?.category)

  const extendedRadius = useMemo(
    () => getExtendedRadius(location?.area?.radius),
    [location?.area?.radius]
  )
  const isZeroSrp = useMemo(() => isZeroInventorySearch(totalListings), [totalListings])
  const isLowSrp = useMemo(() => isLowInventorySearch(totalListings), [totalListings])

  // We are interested in the user's location here, not the location of the listing.
  const isICOActive = useMemo(
    () => isICOLaunchedInLocation(searchQuery?.location?.locationPaths),
    [searchQuery?.location?.locationPaths]
  )
  const searchProvinceId =
    searchQuery?.location?.locationPaths && searchQuery?.location?.locationPaths.length > 1
      ? searchQuery.location.locationPaths[1].id
      : undefined

  const { listingsWithCrossPromotion } = useSearchListCrossPromoConfig(listings, category)

  // We want to check the category the user is searching in, not the category
  // of the listing.
  const isCarsTrucksSearch = useMemo(() => isCarsTrucks(category.id), [category.id])
  const listingsGap = isCarsTrucksSearch ? autosSearchListContainerGap : undefined

  const searchPageType = useMemo(() => getSearchPageType(globalKeyword), [globalKeyword])

  const [adsenseRemovalDecision] = useDecision(FEATURE_FLAG.ADSENSE_REMOVAL_EXP)
  const isAdsenseRemoved =
    adsenseRemovalDecision?.enabled === true && adsenseRemovalDecision?.variationKey === 'b'

  useEffect(() => {
    if (isLowSrp) {
      trackEvent({ action: searchPageType, label: 'SuggestionBoxLoad' })
    }
  }, [isLowSrp, searchPageType])

  return (
    <Flex flexDirection="column" gap={spacing.default}>
      <>
        {loading ? (
          <SrpLoading variant="listing-card" />
        ) : (
          <>
            <ImageSeoJsonLdWrapper
              listings={listings.concat(topAds)}
              id="srp-listing-card-image-json-ld"
            />

            {/* Provincial Top Ads (PTAs) need to display on ZSRPs */}
            <RenderTopAds
              globalCategoryId={category.id}
              topAds={topAds}
              isICOActive={isICOActive}
              isMobile={isMobile}
              listingsGap={listingsGap}
              totalListings={totalListings}
            />

            <PopularRelatedKeywords
              categoryId={category.id.toString()}
              keywords={searchQuery?.keywords}
              location={{ id: location.id }}
              locationArea={location.area}
            />
            {isZeroSrp && <ZeroResultsActionBox />}

            {!!listings.length && (
              <SearchListContainer data-testid="srp-search-list" gap={listingsGap}>
                {listings.map((item, index) => (
                  <RenderListingCard
                    key={item.id}
                    globalCategoryId={category.id}
                    item={item}
                    index={index}
                    isICOActive={isICOActive}
                    isCrossPromotionCard={listingsWithCrossPromotion?.includes(item.id)}
                    isMobile={isMobile}
                    totalListings={totalListings}
                    searchProvinceId={searchProvinceId}
                  />
                ))}
              </SearchListContainer>
            )}

            {isLowSrp && (
              <>
                {hasExtendedRadiusListings(extendedRadiusListings.length, extendedRadius) && (
                  <>
                    <Spacing mTop="2rem" mBottom="1rem">
                      <HeadlineText
                        color={colors.grey.primary}
                        size="xLarge"
                        weight="medium"
                        as="h2"
                      >
                        {t('srp:extended_listings.title', { extendedRadius })}
                      </HeadlineText>
                    </Spacing>

                    <SearchListContainer data-testid="srp-extended-list" gap={listingsGap}>
                      {extendedRadiusListings.map((item, index) => (
                        <RenderListingCard
                          key={item.id}
                          globalCategoryId={category.id}
                          item={item}
                          index={index}
                          totalListings={extendedRadiusListings.length}
                          isICOActive={isICOActive}
                          isMobile={isMobile}
                          searchProvinceId={searchProvinceId}
                        />
                      ))}
                    </SearchListContainer>
                  </>
                )}

                <Spacing mTop="6rem">
                  <ZeroResultsActionBox />
                </Spacing>
              </>
            )}
          </>
        )}
      </>
      {/* Minileaderboard should not appear on zSRP */}
      {!isAdsenseRemoved && (
        <AdSense id="AFSBottom">{!!totalListings && <MiniLeaderboardAdSlot />}</AdSense>
      )}

      <PaginationLinkContainer justifyContent="center" alignItems="center">
        {totalPageCount > 1 && (
          <SrpPagination selectedIndex={currentPage} totalPageCount={totalPageCount} />
        )}
      </PaginationLinkContainer>
    </Flex>
  )
}
